/**
 * Renders a CMS page by resolving it from Firestore (home setting or slug),
 * applies SEO overrides, and renders title + body (HTML).
 */

import { useEffect, useState } from 'react';
import {
  getHomePage,
  getCMSPageBySlug,
  getSEOOverrideByPath,
  getSEOOverrideByPageId,
  type CMSPage,
} from '@/lib/cms';
import { setPageMeta } from '@/utils/metaTags';
import { CMSBodyRenderer } from '@/components/CMSBodyRenderer';
import { getPlaceholderReplacements, replaceCmsPlaceholders } from '@/lib/cms-placeholders';
import { HomePage } from './HomePage';
import { TheCrisisPage } from './TheCrisisPage';

interface BrandingSettings {
  brandName?: string;
  brandTagline?: string;
  logoUrl?: string;
  metaDescription?: string;
}

interface ContactSettings {
  contactPhone?: string;
  contactEmail?: string;
  companyAddress?: string;
}

interface CMSPageViewProps {
  /** Current path, e.g. '/' or '/lp/promo'. */
  path: string;
  /** For /lp/:slug routes, the slug param. */
  slug?: string | null;
  /** Branding fallback for meta (e.g. brandName for default title). */
  brandName?: string | null;
  /** Full branding for designed home page when no CMS home is set. */
  branding?: BrandingSettings | null;
  /** Contact/company info from Settings & Branding — used to replace {{phone-number}}, {{contact-email}}, {{company-address}} in CMS body. */
  contact?: ContactSettings | null;
  /** Opens the Book a Strategy Call modal. Passed to static page components when path has no CMS override. */
  onOpenStrategyCall?: () => void;
}

export function CMSPageView({ path, slug, brandName, branding, contact, onOpenStrategyCall }: CMSPageViewProps): JSX.Element {
  const [page, setPage] = useState<CMSPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const isHomePath = path === '/' || !path;

  useEffect(() => {
    let cancelled = false;
    const LOAD_TIMEOUT_MS = 8000;

    async function load() {
      setLoading(true);
      setNotFound(false);
      try {
        let p: CMSPage | null = null;
        if (isHomePath) {
          p = await Promise.race([
            getHomePage(),
            new Promise<null>((_, reject) =>
              setTimeout(() => reject(new Error('Home page load timeout')), LOAD_TIMEOUT_MS)
            ),
          ]);
        } else if (slug) {
          p = await getCMSPageBySlug(slug);
        }
        if (cancelled) return;
        if (!p) {
          setPage(null);
          setNotFound(true);
          setLoading(false);
          return;
        }
        setPage(p);

        const [seoByPath, seoByPageId] = await Promise.all([
          getSEOOverrideByPath(path),
          getSEOOverrideByPageId(p.id),
        ]);
        if (cancelled) return;
        const merged = seoByPageId ?? seoByPath ?? null;

        const baseUrl = window.location.origin;
        const url = isHomePath ? baseUrl : `${baseUrl}${path}`;
        setPageMeta({
          title: merged?.title ?? p.title,
          description: merged?.description ?? (p.body.slice(0, 160).replace(/<[^>]*>/g, '') || undefined),
          keywords: merged?.keywords ?? undefined,
          url,
          image: merged?.ogImage ?? undefined,
          ogTitle: merged?.ogTitle ?? p.title,
          ogDescription: merged?.ogDescription ?? undefined,
          twitterCard: merged?.twitterCard ?? undefined,
          twitterTitle: merged?.twitterTitle ?? p.title,
          twitterDescription: merged?.twitterDescription ?? undefined,
          canonical: merged?.canonical ?? url,
          noIndex: merged?.noIndex ?? false,
          schemaJson: merged?.schemaJson ?? undefined,
        });
      } catch (err) {
        const isHomeTimeout =
          isHomePath &&
          err instanceof Error &&
          err.message === 'Home page load timeout';
        if (isHomeTimeout) {
          console.debug('[CMSPageView] Using static home page (CMS load timed out or unavailable).');
        } else {
          console.error('[CMSPageView] Error loading page:', err);
        }
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [path, slug, isHomePath]);

  // For home path, show static HomePage immediately so the site never blocks on Firestore
  if (loading && isHomePath) {
    return (
      <HomePage
        branding={branding ?? { brandName: brandName ?? 'GSGO' }}
        onOpenStrategyCall={onOpenStrategyCall}
      />
    );
  }



  if (loading) {
    return (
      <main id="main-content" className="w-full px-4 py-12" role="main" aria-busy="true">
        <div className="animate-pulse space-y-4 max-w-4xl mx-auto">
          <div className="h-8 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-full" />
          <div className="h-4 bg-gray-200 rounded w-5/6" />
        </div>
      </main>
    );
  }

  if (notFound || !page) {
    if (isHomePath) {
      return (
        <HomePage
          branding={branding ?? { brandName: brandName ?? 'GSGO' }}
          onOpenStrategyCall={onOpenStrategyCall}
        />
      );
    }

    if (slug === 'the-crisis') {
      return <TheCrisisPage />;
    }
 
    return (
      <main id="main-content" className="w-full px-4 py-12" role="main">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-gray-900">Page not found</h1>
          <p className="mt-2 text-gray-600">
            The page you're looking for doesn't exist or hasn't been published yet.
          </p>
        </div>
      </main>
    );
  }

  const replacements = getPlaceholderReplacements(contact, branding);
  const bodyWithPlaceholders = replaceCmsPlaceholders(page.body || '', replacements);

  return (
    <main id="main-content" className="w-full" role="main" aria-label={page.title}>
      <div>
        {page.showTitle !== false && (
          <h1 className="text-3xl font-bold text-gray-900 mb-6">{page.title}</h1>
        )}
        <CMSBodyRenderer body={bodyWithPlaceholders} />
      </div>
    </main>
  );
}
